import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { fetchUserById, User, getUserProfileAPI, UserProfile } from './userAPI'; // Import the API functions

interface UserState {
  info: User | null;
  profile: UserProfile | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  profileStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: UserState = {
  info: null,
  profile: null,
  status: 'idle',
  profileStatus: 'idle',
  error: null,
};

// Async thunk for fetching user profile from API
export const fetchUserProfile = createAsyncThunk(
  'user/fetchUserProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getUserProfileAPI();
      if (response.status && response.data) {
        return response.data;
      } else {
        return rejectWithValue(response.errors || 'Failed to fetch user profile');
      }
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching user data using our mock API
export const fetchUser = createAsyncThunk(
  'user/fetchUser',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await fetchUserById(userId);
      return response.data;
    } catch (error: any) {
      // Use rejectWithValue to return a specific error payload
      return rejectWithValue(error.message);
    }
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearUser: (state) => {
      state.info = null;
      state.profile = null;
      state.status = 'idle';
      state.profileStatus = 'idle';
      state.error = null;
    },
    updateProfile: (state, action: PayloadAction<UserProfile>) => {
      state.profile = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch user profile cases
      .addCase(fetchUserProfile.pending, (state) => {
        state.profileStatus = 'loading';
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.profileStatus = 'succeeded';
        if (action.payload) {
          state.profile = action.payload;
        }
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.profileStatus = 'failed';
        state.error = action.payload as string;
      })
      // Fetch user cases
      .addCase(fetchUser.pending, (state) => {
        state.status = 'loading';
        state.error = null; // Clear previous errors
      })
      .addCase(fetchUser.fulfilled, (state, action: PayloadAction<User>) => {
        state.status = 'succeeded';
        state.info = action.payload;
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string || 'Failed to fetch user';
      });
  },
});

export const { clearUser, updateProfile } = userSlice.actions;

export const userReducer = userSlice.reducer;

export const selectUserInfo = (state: RootState) => state.user.info;
export const selectUserProfile = (state: RootState) => state.user.profile;
export const selectUserStatus = (state: RootState) => state.user.status;
export const selectProfileStatus = (state: RootState) => state.user.profileStatus;
export const selectUserError = (state: RootState) => state.user.error;